import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { useCallIntelColumnsAccess } from '@frontend/call-intel-core';
import { formatDate } from '@frontend/date';
import { largeDownloadsRef } from '@frontend/file-download-helper';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  ChecklistMenuField,
  DateRangeField,
  Modal,
  Text,
  useAlert,
  useFormField,
} from '@frontend/design-system';
import { defaultDateRangeMap, getTimePeriodFilterLabels } from '../../utils';
import { DEFAULT_NO_TIME_PERIOD, genericDays, TimePeriodSelector } from '../filter-selectors/time-period-selector';

const MAX_DATE_RANGE_DAYS = 31;

export const HistoryExportModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation('calls');
  const { selectedLocationIds, getScopeName, accessibleLocationData } = useAppScopeStore();
  const isMultiLocation = selectedLocationIds.length > 1;

  const minDate = useMemo(() => {
    if (!isMultiLocation) return '';
    const locationMinDates = selectedLocationIds.map((locationId) => {
      const location = accessibleLocationData[locationId];
      return location?.createdAt ? new Date(location.createdAt) : new Date();
    });

    const earliestDate = new Date(Math.min(...locationMinDates.map((date) => date.getTime())));
    const earliestDateString = earliestDate.toISOString();

    return earliestDateString;
  }, [isMultiLocation, selectedLocationIds, accessibleLocationData]);

  const [showCustomDateRangeSelector, setShowCustomDateRangeSelector] = useState(false);
  const alerts = useAlert();

  const [selectedDates, setSelectedDates] = useState({
    startDate: '',
    endDate: '',
    selectedPeriod: DEFAULT_NO_TIME_PERIOD,
  });

  const checklistProps = useFormField({
    type: 'checklist',
    value: [],
  });

  const locationIds = isMultiLocation ? checklistProps.value : selectedLocationIds;

  const { showCallIntelInsights } = useCallIntelColumnsAccess({ locationIds });

  const customRender = useCallback(
    (props: { selectedKeys: string[]; selectedLabels: string[]; keyToLabelMap: Record<string, string> }) => (
      <ChecklistMenuField.ChipInput
        {...props}
        variant='primaryDark'
        onRemoveClick={(id) => {
          checklistProps.onChange?.({
            name: 'locationIds',
            value: [id],
          });
        }}
      />
    ),
    [checklistProps]
  );

  const { customPeriodGroups, filterLabels } = useMemo(() => {
    const labels = getTimePeriodFilterLabels();
    // when we can export larger data sets in the future (60/90 days, 1 year, etc),
    // we can remove this limitation
    // and just add the defaultDateRangeMap directly + custom
    const limitedDateRangeMap = {
      [DEFAULT_NO_TIME_PERIOD]: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD],
      today: defaultDateRangeMap.today,
      yesterday: defaultDateRangeMap.yesterday,
      'last-7-days': defaultDateRangeMap['last-7-days'],
      'last-14-days': defaultDateRangeMap['last-14-days'],
      'last-30-days': defaultDateRangeMap['last-30-days'],
    };
    const limitedLabels = {
      today: labels.today,
      yesterday: labels.yesterday,
      'last-7-days': labels['last-7-days'],
      'last-14-days': labels['last-14-days'],
      'last-30-days': labels['last-30-days'],
    };
    const limitedGenericDays = genericDays.filter((day) =>
      ['today', 'yesterday', 'last-7-days', 'last-14-days', 'last-30-days'].includes(day)
    );

    return {
      filterLabels: limitedLabels,
      customPeriodGroups: {
        datesMap: {
          ...limitedDateRangeMap,
          custom: { startDate: '', endDate: '' },
        },
        labels: {
          ...limitedLabels,
          custom: i18next.t('Custom', { ns: 'calls' }),
        },
        groups: [
          {
            period: ['custom', ...limitedGenericDays],
          },
        ],
      },
    };
  }, []);

  const dateRangeProps = useFormField(
    {
      type: 'dateRange',
      maxDate: defaultDateRangeMap['today'].endDate,
      minDate: minDate ? minDate : undefined,
      value: [],
      validator: (dateRangeField) => {
        const value = dateRangeField.value;

        if (!value[0]) {
          return '';
        }

        if (value[0] && value[1]) {
          const startDate = dayjs(value[0]);
          const endDate = dayjs(value[1]);

          // Check if end date is after start date
          if (endDate.isBefore(startDate)) {
            return t('End date must be after the start date');
          }

          // Check for maximum date range
          const daysDifference = endDate.diff(startDate, 'days');
          if (daysDifference > MAX_DATE_RANGE_DAYS) {
            return t('Date Range must be {{maxRange}} days or less. You have {{currentRange}} days selected.', {
              maxRange: MAX_DATE_RANGE_DAYS,
              currentRange: daysDifference,
            });
          }
        }

        return '';
      },
    },
    []
  );

  const { mutate } = useMutation(
    async ({
      includeCallIntel,
      selectedDateRange,
      targetLocationIds,
      selectedCustomStartDate,
      selectedCustomEndDate,
    }: {
      includeCallIntel?: boolean;
      targetLocationIds: string[];
      selectedDateRange: string[];
      selectedCustomStartDate: string;
      selectedCustomEndDate: string;
    }) => {
      let startTime: string;
      let endTime: string;

      if (showCustomDateRangeSelector) {
        const [startDate, endDate] = selectedDateRange;
        startTime = new Date(startDate).toISOString();
        endTime = new Date(endDate).toISOString();
      } else {
        startTime = new Date(selectedCustomStartDate).toISOString();
        endTime = new Date(selectedCustomEndDate).toISOString();
      }

      const res = await PhoneCallsApi.exportCallRecords({
        locationIds: targetLocationIds,
        startTime,
        endTime,
        includeCallIntel,
      }).then((response) => {
        return {
          ...response,
          startTime,
          endTime,
        };
      });

      return res;
    },
    {
      onSuccess: ({ exportId, startTime, endTime }) => {
        onClose();
        const startTimeFormatted = formatDate(new Date(startTime), 'MMM D, YYYY');
        const endTimeFormatted = formatDate(new Date(endTime), 'MMM D, YYYY');
        largeDownloadsRef.current?.addEventInProgress({
          id: exportId,
          url: `/phone-exp/phone-records/v1/export/call-records/${exportId}/status`,
          fileExtension: 'csv',
          fileName: `call-history-${startTimeFormatted}-${endTimeFormatted}`,
        });
      },
      onError: () => {
        alerts.error(t('An error occurred while preparing your download. Please try again later.'));
      },
    }
  );

  const dateBuilderProps = useMemo(() => {
    const label =
      selectedDates.selectedPeriod === 'custom'
        ? i18next.t('Custom Range')
        : (filterLabels as Record<string, string>)[selectedDates.selectedPeriod as string];
    return {
      startDate: selectedDates.startDate || dateRangeProps.value[0],
      endDate: selectedDates.endDate || dateRangeProps.value[1],
      label,
    };
  }, [
    dateRangeProps.value,
    filterLabels,
    selectedDates.endDate,
    selectedDates.selectedPeriod,
    selectedDates.startDate,
  ]);

  const isDateRangeValid =
    (!!selectedDates.startDate && !!selectedDates.endDate) ||
    (dateRangeProps.value.length === 2 && dateRangeProps.value[0] !== '' && dateRangeProps.value[1] !== '');

  const hasValidLocations = !isMultiLocation || checklistProps.value.length > 0;

  const onChangeTimePeriod = useCallback(
    ({
      endDate,
      startDate,
      selectedPeriod = DEFAULT_NO_TIME_PERIOD,
    }: {
      endDate: string;
      startDate: string;
      selectedPeriod?: string;
    }) => {
      if (selectedPeriod === 'custom') {
        setShowCustomDateRangeSelector(true);
        setSelectedDates({ startDate: '', endDate: '', selectedPeriod: selectedPeriod as string });
      } else {
        setShowCustomDateRangeSelector(false);
        setSelectedDates({ startDate, endDate, selectedPeriod: selectedPeriod as string });
        dateRangeProps.onChange?.({
          name: 'dateRange',
          value: [],
        });
      }
    },
    [dateRangeProps]
  );

  const handleDowndloadClick = useCallback(() => {
    mutate({
      includeCallIntel: showCallIntelInsights,
      targetLocationIds: locationIds,
      // used for custom date range
      selectedDateRange: dateRangeProps.value,
      // used for pre-built date ranges (TimePeriodSelector)
      selectedCustomStartDate: selectedDates.startDate,
      selectedCustomEndDate: selectedDates.endDate,
    });
  }, [
    showCallIntelInsights,
    locationIds,
    dateRangeProps.value,
    mutate,
    selectedDates.startDate,
    selectedDates.endDate,
  ]);

  const disabledDownload = !isDateRangeValid || !hasValidLocations || !!dateRangeProps.error;

  const isLocationDisabled = useCallback(
    (locationId: string) => {
      const selectedCount = checklistProps.value.length;
      return selectedCount >= 5 && !checklistProps.value.includes(locationId);
    },
    [checklistProps.value]
  );
  return (
    <>
      <Modal.Header onClose={onClose}>{t('Download Data to CSV')}</Modal.Header>
      <Modal.Body>
        <BannerNotification
          status='info'
          message={
            <Text>
              {t(
                'Before downloading the CSV, please select your desired criteria for the Call History data. Filters that are currently applied to the Call History table will not transfer to the CSV file.'
              )}
            </Text>
          }
        />
        <Text weight='bold'>{t('Date Range')}</Text>
        <TimePeriodSelector
          defaultPeriod={DEFAULT_NO_TIME_PERIOD}
          onChange={onChangeTimePeriod}
          hideDropdownIcon
          customPeriodGroups={customPeriodGroups}
          placeholder={t('Select date range')}
          label={t('Select date range')}
        />
        {showCustomDateRangeSelector && (
          <>
            <DateRangeField
              {...dateRangeProps}
              label={t('Time Range Date')}
              name='download-csv-custom-date-range'
              css={[!!dateRangeProps.error && styles.dateRangeFieldError]}
            />
            {!!dateRangeProps.error && (
              <Text size='small' color='error'>
                {dateRangeProps.error}
              </Text>
            )}
          </>
        )}
        <DateBuilder {...dateBuilderProps} />
        {isMultiLocation && (
          <>
            <Text weight='bold'>{t('Locations')}</Text>
            <ChecklistMenuField
              {...checklistProps}
              label={t('Location(s)')}
              placeholder={t('Location(s)')}
              name='locationIds'
              customRender={customRender}
              showSelectionBar={false}
            >
              {selectedLocationIds.map((locationId) => (
                <ChecklistMenuField.Option
                  disabled={isLocationDisabled(locationId)}
                  disabledMessage={t('You have reached the maximum number of locations')}
                  key={locationId}
                  value={locationId}
                >
                  {getScopeName(locationId)}
                </ChecklistMenuField.Option>
              ))}
            </ChecklistMenuField>
          </>
        )}
      </Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Close'),
          onClick: onClose,
        }}
        primary={{
          label: t('Download CSV'),
          onClick: handleDowndloadClick,
          disabled: disabledDownload,
          trackingId: 'callhistory-CSV-downloadconfirm',
        }}
      />
    </>
  );
};

const styles = {
  dateRangeFieldError: css`
    label {
      color: ${theme.colors.critical50};
    }
    div > div > div {
      border-color: ${theme.colors.critical50};
    }
  `,
};

const DateBuilder = ({ startDate, endDate, label }: { startDate: string; endDate: string; label: string }) => {
  const { t } = useTranslation('calls');

  if (!startDate || !endDate || !label) return null;
  const startTime = formatDate(new Date(startDate), 'ddd MMM D, YYYY');
  const endTime = formatDate(new Date(endDate), 'ddd MMM D, YYYY');

  return (
    <Text css={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Text as='span'>{`${label}:`}</Text>
      <Icon name='calendar' size={16} />
      <Text as='span'>{startTime}</Text>
      <Text as='span'>{t('to')}</Text>
      <Icon name='calendar' size={16} />
      <Text as='span'>{endTime}</Text>
    </Text>
  );
};
